/**
 * useModules — the activation layer, wired to the current org (mod-2).
 *
 * Reads `orgs.org_config.enabled_modules` (+ `tax_regime`) off the cached
 * currentOrg and resolves the active module set via the pure `resolveModules`
 * (docs/MODULES.md Sec. 3). Also exposes the write path (activate / deactivate /
 * set) — the FIRST explicit write snapshots the org's current effective
 * operational set, so the moment of opt-in is invisible (legacy absence
 * semantics, guardrail 4).
 *
 * Guardrails: this hook drives UX only. Activation is never a security boundary
 * (guardrail 1) — RLS/RBAC/entitlements remain authoritative. Compliance modules
 * are auto-resolved from facts and cannot be written here (guardrail 2).
 *
 * @since mod-2
 */
import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/db/supabase';
import {
    resolveModules,
    getModuleDef,
    OPERATIONAL_MODULES,
    requiresClosure,
    type ModuleProfile,
} from '../config/modules';

const OPERATIONAL_IDS = new Set(OPERATIONAL_MODULES.map((m) => m.id));

export interface UseModulesResult {
    /** Active NON-core module ids (compliance + operational). Core is always on. */
    active: ReadonlySet<string>;
    isActive: (moduleId: string) => boolean;
    /** The org's profile facts driving the compliance tier. */
    profile: ModuleProfile;
    /** `org_config.enabled_modules` — undefined ⇒ legacy org (everything on). */
    enabled: string[] | undefined;
    /** True until the org explicitly interacts (enabled_modules is absent). */
    isLegacy: boolean;
    /** ISO date of the first modules interaction (drives the one-time nudge, mod-4). */
    seededAt: string | null;
    ready: boolean;
    /** Turn a module on (operational only). First write snapshots the effective set. */
    activateModule: (moduleId: string) => Promise<{ error: Error | null }>;
    /** Turn a module off (operational only). Dependents are pulled off with it. */
    deactivateModule: (moduleId: string) => Promise<{ error: Error | null }>;
    /** Replace the explicit operational list wholesale (gallery bulk save, mod-3). */
    setEnabledModules: (nextOperational: string[]) => Promise<{ error: Error | null }>;
    /**
     * Legacy opt-out (mod-4): record that the org has seen the modules layer
     * WITHOUT choosing a list — stays legacy-all-on, but the one-time nudge and
     * the Get-Started step go quiet. No-op once already seeded.
     */
    markSeeded: () => Promise<{ error: Error | null }>;
}

export function useModules(): UseModulesResult {
    const currentOrg = useAuth((s) => s.currentOrg);
    const updateOrgLocal = useAuth((s) => s.updateOrgLocal);
    const orgId = currentOrg?.id;

    const orgConfig = useMemo(
        () => (currentOrg?.org_config ?? {}) as Record<string, unknown>,
        [currentOrg?.org_config],
    );

    const enabled = useMemo(() => {
        const raw = orgConfig.enabled_modules;
        return Array.isArray(raw) ? (raw as string[]) : undefined;
    }, [orgConfig]);

    const seededAt = typeof orgConfig.modules_seeded_at === 'string' ? (orgConfig.modules_seeded_at as string) : null;

    const profile: ModuleProfile = useMemo(
        () => ({
            businessNature: currentOrg?.business_nature ?? null,
            orgType: currentOrg?.org_type ?? null,
            taxRegime: (orgConfig.tax_regime as string | undefined) ?? null,
            country: currentOrg?.country ?? null,
        }),
        [currentOrg?.business_nature, currentOrg?.org_type, currentOrg?.country, orgConfig],
    );

    const active = useMemo(() => resolveModules(profile, enabled), [profile, enabled]);

    const isActive = useCallback((moduleId: string) => active.has(moduleId), [active]);

    /** Currently-active operational ids (the snapshot base for a first write). */
    const currentOperational = useMemo(
        () => [...active].filter((id) => OPERATIONAL_IDS.has(id)),
        [active],
    );

    /** Persist a new explicit operational list, merging over the existing org_config. */
    const persist = useCallback(
        async (nextOperational: string[]): Promise<{ error: Error | null }> => {
            if (!orgId) return { error: new Error('No organization selected') };
            // Store the user's CHOICE (not the requires-closure) — closure is applied
            // at resolve time so dependencies can't be orphaned by a later toggle.
            const cleaned = [...new Set(nextOperational.filter((id) => OPERATIONAL_IDS.has(id)))];
            const nextConfig = {
                ...orgConfig,
                enabled_modules: cleaned,
                modules_seeded_at: seededAt ?? new Date().toISOString(),
            };
            const { error } = await supabase.from('orgs').update({ org_config: nextConfig }).eq('id', orgId);
            if (error) return { error: new Error(error.message) };
            updateOrgLocal(orgId, { org_config: nextConfig });
            return { error: null };
        },
        [orgId, orgConfig, seededAt, updateOrgLocal],
    );

    const setEnabledModules = useCallback(
        (nextOperational: string[]) => persist(nextOperational),
        [persist],
    );

    const activateModule = useCallback(
        (moduleId: string) => {
            if (!OPERATIONAL_IDS.has(moduleId)) {
                return Promise.resolve({ error: new Error('Only operational modules can be activated') });
            }
            // First write snapshots the current effective operational set (absence
            // semantics), then adds the new module.
            const base = enabled ?? currentOperational;
            return persist([...base, moduleId]);
        },
        [enabled, currentOperational, persist],
    );

    const deactivateModule = useCallback(
        (moduleId: string) => {
            if (!OPERATIONAL_IDS.has(moduleId)) {
                return Promise.resolve({ error: new Error('Only operational modules can be deactivated') });
            }
            const base = enabled ?? currentOperational;
            // Drop the module AND anything that requires it (keeps the closure honest).
            const dependents = OPERATIONAL_MODULES
                .filter((m) => requiresClosure([m.id]).has(moduleId) && m.id !== moduleId)
                .map((m) => m.id);
            const drop = new Set([moduleId, ...dependents]);
            return persist(base.filter((id) => !drop.has(id)));
        },
        [enabled, currentOperational, persist],
    );

    const markSeeded = useCallback(async (): Promise<{ error: Error | null }> => {
        if (!orgId) return { error: new Error('No organization selected') };
        if (seededAt) return { error: null }; // already interacted — nothing to do
        const nextConfig = { ...orgConfig, modules_seeded_at: new Date().toISOString() };
        const { error } = await supabase.from('orgs').update({ org_config: nextConfig }).eq('id', orgId);
        if (error) return { error: new Error(error.message) };
        updateOrgLocal(orgId, { org_config: nextConfig });
        return { error: null };
    }, [orgId, orgConfig, seededAt, updateOrgLocal]);

    return {
        active,
        isActive,
        profile,
        enabled,
        isLegacy: enabled === undefined,
        seededAt,
        ready: !!currentOrg?.id,
        activateModule,
        deactivateModule,
        setEnabledModules,
        markSeeded,
    };
}

/** Re-export for convenience so callers can label an off-module route. */
export { getModuleDef };
